import type { CompStage, CompStageStatus, CompStageType, MatchSummary } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// groupMatchesByStage — pure grouping logic for the Matches step's
// MatchScheduleBoard (TournamentSetup.tsx). Takes the SAME two reads the rest
// of the pipeline UI already uses (GET /matches, GET .../stages) and shapes
// them into "one section per stage, in pipeline order, each optionally
// sub-grouped by round" — no new endpoint, no new backend field.
//
// Round detection is per-engine, because each stage-generation engine stamps
// a DIFFERENT round field onto its MATCH# rows (see index.ts):
//   - SEEDING            -> roundNumber      (writeSeedingRound)
//   - DIVISIONS          -> groupIndex + groupRound (generateDivisionsPlay /
//                            legacy POST /comps/:id/groupstage/init), grouped
//                            into WEEKS per the stage's "games per week"
//                            config (see groupByDivisionWeek below) — one
//                            round-robin round is "every team plays once", so
//                            gamesPerWeek=N means N rounds per week. SOFT
//                            guide only, default 1 (one round == one week).
//   - SWISS              -> swissRound       (generateSwissPlay / legacy
//                            POST /comps/:id/swiss/pair)
//   - ROUND_ROBIN        -> no round field — schedule generates once, so
//                            there's nothing to sub-group by.
//   - ELIMINATION_SINGLE/
//     ELIMINATION_DOUBLE  -> NEITHER — the whole stage plays out as BRACKET#
//                            slot rows (BracketSlot: slot/teamA/teamB/winner/
//                            status), never a MATCH# row at all. GET /matches
//                            only queries begins_with(sk, 'MATCH#'), so these
//                            stages will ALWAYS show zero matches from this
//                            board's data source — `isBracketStage` flags
//                            this so the UI can say so honestly instead of
//                            silently rendering an empty stage. The bracket
//                            itself is tracked by EliminationBracketPanel
//                            (Setup → Competition → stage pipeline), which
//                            reads the BRACKET# rows directly.
// ─────────────────────────────────────────────────────────────────────────────

export interface MatchRoundGroup {
  key: string;
  // null => no round distinction for this stage type (render matches flat,
  // e.g. ROUND_ROBIN, legacy/unassigned, or a stage with exactly one round).
  label: string | null;
  matches: MatchSummary[];
}

export interface StageMatchGroup {
  key: string;
  stageId: string | null;
  stageType: CompStageType | null;
  stageStatus: CompStageStatus | null;
  ordinal: number;
  isBracketStage: boolean;
  matches: MatchSummary[];
  rounds: MatchRoundGroup[];
}

const LEGACY_ORDINAL = Number.MAX_SAFE_INTEGER;

export interface GroupMatchesByStageOptions {
  // Wall #2 (standalone Matches board, Matches.tsx) reads newest-played-first
  // — an admin scoring last night's games should see them at the top. Setup's
  // MatchScheduleBoard has always read chronologically (whatever order/round
  // number ascends); recentFirst defaults to false so that board's existing
  // order is untouched byte-for-byte. When true: round/division/week groups
  // order with the LATEST leading (division letter order stays stable — it
  // isn't chronological), and matches within a group sort descending by
  // scheduledAt ?? createdAt ?? 0.
  recentFirst?: boolean;
}

// "Div A" — the SAME letter mapping DIVISIONS grouping has always used
// (groupIndex is 0-based, letters are display-only). Exported as the single
// authority so the per-row division tag (Matches.tsx's MatchLinkRow,
// MatchRow.tsx) never drifts from whatever this module itself would've
// labelled a division.
export function divisionLetter(groupIndex: number): string {
  return String.fromCharCode(65 + groupIndex);
}

// ── Week-grouping math — REPLICATED from src/shared/divisionSchedule.ts's
// weekForRound/weekDateRange/DEFAULT_CADENCE_DAYS (backend, pure, no AWS
// imports), not imported: the frontend is a fully separate TypeScript/Vite
// project (own package.json/tsconfig rooted at frontend/src, own build
// pipeline) with no workspace wiring back to the backend's src/shared — see
// frontend/src/lib/groupPlan.ts's doc comment for the established precedent
// of replicating rather than reaching across that boundary. Kept tiny and
// verbatim so it's trivial to eyeball against the source if that ever
// changes.
const DEFAULT_CADENCE_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function weekForRound(round: number, roundsPerWeek: number): number {
  const perWeek = Math.max(1, Math.floor(roundsPerWeek));
  return Math.ceil(round / perWeek);
}

function weekDateRange(round: number, scheduleStartAt: number, roundsPerWeek: number): { start: number; end: number } {
  const cadenceMs = DEFAULT_CADENCE_DAYS * MS_PER_DAY;
  const start = scheduleStartAt + (weekForRound(round, roundsPerWeek) - 1) * cadenceMs;
  return { start, end: start + cadenceMs };
}

function formatWeekDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function groupMatchesByStage(
  matches: MatchSummary[],
  stages: CompStage[],
  opts: GroupMatchesByStageOptions = {},
): StageMatchGroup[] {
  const recentFirst = opts.recentFirst ?? false;
  const orderedStages = [...stages].sort((a, b) => a.ordinal - b.ordinal);
  const knownStageIds = new Set(orderedStages.map(s => s.stageId));

  const byStageId = new Map<string, MatchSummary[]>();
  const legacy: MatchSummary[] = [];

  for (const m of matches) {
    if (m.stageId && knownStageIds.has(m.stageId)) {
      const list = byStageId.get(m.stageId) ?? [];
      list.push(m);
      byStageId.set(m.stageId, list);
    } else {
      // No stageId (pre-pipeline / manually created), or a stageId that no
      // longer matches any composed stage (e.g. the stage was removed since)
      // — both fold into the same catch-all bucket rather than vanishing.
      legacy.push(m);
    }
  }

  // Stage SECTIONS always stay in pipeline order (ordinal ascending) even in
  // recentFirst mode — only the round/division axis inside a stage, and the
  // matches inside a round, flip to newest-first.
  const groups: StageMatchGroup[] = orderedStages.map(s => {
    const stageMatches = byStageId.get(s.stageId) ?? [];
    return {
      key: s.stageId,
      stageId: s.stageId,
      stageType: s.type,
      stageStatus: s.status,
      ordinal: s.ordinal,
      isBracketStage: s.type === 'ELIMINATION_SINGLE' || s.type === 'ELIMINATION_DOUBLE',
      matches: stageMatches,
      rounds: groupByRound(stageMatches, recentFirst, s),
    };
  });

  if (legacy.length > 0 || groups.length === 0) {
    groups.push({
      key: 'legacy',
      stageId: null,
      stageType: null,
      stageStatus: null,
      ordinal: LEGACY_ORDINAL,
      isBracketStage: false,
      matches: legacy,
      // No owning stage for the catch-all bucket (see the loop above) — a
      // stray DIVISIONS-shaped match here (e.g. its stage was since removed)
      // still groups by week, just with no gamesPerWeek/schedule anchor to
      // read, i.e. the soft default of 1 round per week.
      rounds: groupByRound(legacy, recentFirst, undefined),
    });
  }

  return groups;
}

function groupByRound(matches: MatchSummary[], recentFirst: boolean, stage: CompStage | undefined): MatchRoundGroup[] {
  if (matches.length === 0) return [];

  const hasDivisionRound = matches.some(m => typeof m.groupIndex === 'number' && typeof m.groupRound === 'number');
  if (hasDivisionRound) return groupByDivisionWeek(matches, recentFirst, stage);

  const hasSeedingRound = matches.some(m => typeof m.roundNumber === 'number');
  if (hasSeedingRound) return groupBySimpleRound(matches, m => m.roundNumber, 'Round', recentFirst);

  const hasSwissRound = matches.some(m => typeof m.swissRound === 'number');
  if (hasSwissRound) return groupBySimpleRound(matches, m => m.swissRound, 'Swiss round', recentFirst);

  return [{ key: 'flat', label: null, matches: sortByRecency(matches, recentFirst) }];
}

// No-op when recentFirst is false — preserves the exact original (insertion)
// order MatchScheduleBoard has always rendered.
function sortByRecency(matches: MatchSummary[], recentFirst: boolean): MatchSummary[] {
  if (!recentFirst) return matches;
  return [...matches].sort((a, b) => {
    const at = a.scheduledAt ?? a.createdAt ?? 0;
    const bt = b.scheduledAt ?? b.createdAt ?? 0;
    return bt - at; // descending — most recently played/scheduled leads
  });
}

// DIVISIONS grouping (2026-07-18, "games per week"): the top-level axis is
// now the WEEK, not "Division · Round" — a stage's gamesPerWeek config (SOFT
// organiser guide, default 1) says how many rounds fall in each week, and
// every division's fixtures for that week land in ONE group so a "Week 3"
// section shows the whole slate at a glance, whichever division a team is
// in. Division stays readable WITHIN a week two ways: matches are macro-
// sorted division-then-round (all of Division A's games, then B's, …) and
// each row carries its own "Div A"/"Div B" tag (divisionLetter, rendered by
// Matches.tsx's MatchLinkRow / MatchRow.tsx — a match's groupIndex already
// rides on MatchSummary, so the tag needs no extra plumbing here).
function groupByDivisionWeek(matches: MatchSummary[], recentFirst: boolean, stage: CompStage | undefined): MatchRoundGroup[] {
  const gamesPerWeek = Math.max(1, Math.floor(Number(stage?.config?.gamesPerWeek) || 1));
  // Same anchor precedence as the backend's schedule overlay (P2-c: an
  // admin's plannedStartAt estimate wins over startedAt, the stage's actual
  // go-live) — see shared/divisionSchedule.ts + groupStage.ts's
  // annotateRoundsWithSchedule. Absent entirely (undefined/null) → no
  // schedule anchor yet, so weeks show with no date range at all rather than
  // guessing one.
  const scheduleStartAt = stage?.plannedStartAt ?? stage?.startedAt ?? null;

  const byWeek = new Map<number, MatchSummary[]>();
  for (const m of matches) {
    const week = weekForRound(m.groupRound ?? 0, gamesPerWeek);
    const list = byWeek.get(week) ?? [];
    list.push(m);
    byWeek.set(week, list);
  }

  const weekDir = recentFirst ? -1 : 1;
  return [...byWeek.entries()]
    .sort(([a], [b]) => (a - b) * weekDir)
    .map(([week, weekMatches]) => {
      // Macro-order: division ascending, then round ascending — readable
      // even when several divisions/rounds share one week. recentFirst only
      // decides WHICH WEEK leads (above); within a division+round cluster it
      // still governs fine-grained order, exactly as it did back when that
      // cluster was its own top-level group (pre-week-grouping).
      const clusters = new Map<string, MatchSummary[]>();
      for (const m of weekMatches) {
        const clusterKey = `${m.groupIndex ?? 0}:${m.groupRound ?? 0}`;
        const list = clusters.get(clusterKey) ?? [];
        list.push(m);
        clusters.set(clusterKey, list);
      }
      const orderedMatches = [...clusters.entries()]
        .sort(([a], [b]) => {
          const [aGroup, aRound] = a.split(':').map(Number);
          const [bGroup, bRound] = b.split(':').map(Number);
          return aGroup - bGroup || aRound - bRound;
        })
        .flatMap(([, list]) => sortByRecency(list, recentFirst));

      let label = `Week ${week}`;
      if (scheduleStartAt != null) {
        // Any round that maps back to THIS week anchors the date window —
        // the first round of the week is the simplest such round.
        const anchorRound = (week - 1) * gamesPerWeek + 1;
        const { start, end } = weekDateRange(anchorRound, scheduleStartAt, gamesPerWeek);
        label += ` · ${formatWeekDate(start)} – ${formatWeekDate(end - 1)}`;
      }

      return { key: `week-${week}`, label, matches: orderedMatches };
    });
}

function groupBySimpleRound(
  matches: MatchSummary[],
  getRound: (m: MatchSummary) => number | undefined,
  labelPrefix: string,
  recentFirst: boolean,
): MatchRoundGroup[] {
  const byRound = new Map<number, MatchSummary[]>();
  for (const m of matches) {
    const round = getRound(m) ?? 0;
    const list = byRound.get(round) ?? [];
    list.push(m);
    byRound.set(round, list);
  }
  const dir = recentFirst ? -1 : 1;
  return [...byRound.entries()]
    .sort(([a], [b]) => (a - b) * dir)
    .map(([round, list]) => ({ key: String(round), label: `${labelPrefix} ${round}`, matches: sortByRecency(list, recentFirst) }));
}
